/**
 * ⚡ Request Debouncing Utilities
 * Prevents duplicate API calls and improves performance
 */
import logger from './logger';

interface PendingRequest {
  promise: Promise<any>;
  timestamp: number;
}

const pendingRequests = new Map<string, PendingRequest>();
const REQUEST_DEDUP_MS = 1000; // 1 second deduplication window

/**
 * Deduplicate concurrent requests with the same key
 * If a request with same key is already in flight, return the existing promise
 */
export async function deduplicateRequest<T>(
  key: string,
  requestFn: () => Promise<T>,
  ttlMs: number = REQUEST_DEDUP_MS,
): Promise<T> {
  const now = Date.now();
  const existing = pendingRequests.get(key);

  // Check if we have a recent pending request
  if (existing && now - existing.timestamp < ttlMs) {
    logger.log(`⚡ Deduplicating request: ${key}`);
    return existing.promise as Promise<T>;
  }

  // Create new request
  const promise = requestFn().finally(() => {
    // Clean up after request completes
    setTimeout(() => {
      if (pendingRequests.get(key)?.promise === promise) {
        pendingRequests.delete(key);
      }
    }, ttlMs);
  });

  pendingRequests.set(key, { promise, timestamp: now });
  return promise;
}

/**
 * Debounce function - delays execution until after delay ms have passed
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number,
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      func(...args);
      timeoutId = null;
    }, delay);
  };
}

/**
 * Throttle function - ensures function is called at most once per delay ms
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  delay: number,
): (...args: Parameters<T>) => void {
  let lastCall = 0;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<T>) => {
    const now = Date.now();
    const timeSinceLastCall = now - lastCall;

    if (timeSinceLastCall >= delay) {
      lastCall = now;
      func(...args);
    } else {
      // Schedule call for later
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        lastCall = Date.now();
        func(...args);
        timeoutId = null;
      }, delay - timeSinceLastCall);
    }
  };
}
